import type { View } from '../router/router';
import { apiClient } from '../services/api-client';
import { toast } from '../components/layout/toast';
import { getUTCDateComponents } from '../utils/date-utils';
import { getTyrisIcon } from '../components/ui/tyris-icon';

interface AttendanceStats {
  weeklyCount: number;
  monthlyCount: number;
  allTimeCount: number;
  history?: { id: string; date: string; status: string }[];
}

export class AttendancePage implements View {
  private stats: AttendanceStats | null = null;
  private loading = true;
  private errorMsg: string | null = null;

  async render(): Promise<string> {
    await this.fetchStats();

    if (this.loading) {
      return `
        <div style="display: flex; flex-direction: column; align-items: center; justify-content: center; height: 60vh; gap: 1rem;">
          <div style="color: var(--tyris-energy); animation: spin 1s linear infinite;">${getTyrisIcon('en-curso', { size: 40 })}</div>
          <p style="color: var(--text-muted);">Cargando registro de asistencia...</p>
        </div>
      `;
    }

    if (this.errorMsg) {
      return `
        <div class="card-glass" style="text-align: center; padding: 3rem 1.5rem; margin-top: 2rem;">
          <div style="display: flex; justify-content: center; margin-bottom: 1rem; color: var(--color-error);">${getTyrisIcon('alerta', { size: 48 })}</div>
          <h3 style="font-size: 1.5rem; margin-bottom: 0.5rem;">Error de Sincronización</h3>
          <p style="color: var(--text-muted); margin-bottom: 1.5rem;">${this.errorMsg}</p>
          <button onclick="window.location.reload()" class="btn btn-primary" style="width: auto; padding: 0.75rem 2rem;">Reintentar</button>
        </div>
      `;
    }

    const weekly = this.stats?.weeklyCount ?? 0;
    const monthly = this.stats?.monthlyCount ?? 0;
    const allTime = this.stats?.allTimeCount ?? 0;

    // Calcular nivel de consistencia (gamificado)
    let badgeName = `${getTyrisIcon('comenzar-entrenamiento', { size: 16 })} Iniciante`;
    let badgeColor = 'background-color: rgba(255,255,255,0.05); color: var(--text-muted);';
    let progressPercent = Math.min((monthly / 16) * 100, 100);

    if (monthly >= 16) {
      badgeName = `${getTyrisIcon('racha', { size: 16 })} Leyenda de Acero`;
      badgeColor = 'background-color: rgba(111, 190, 149, 0.2); color: var(--tyris-energy); border: 1px solid rgba(111, 190, 149, 0.4); font-weight: 700;';
    } else if (monthly >= 12) {
      badgeName = `${getTyrisIcon('comenzar', { size: 16 })} Guerrero Consistente`;
      badgeColor = 'background-color: rgba(102, 102, 154, 0.2); color: hsl(250, 95%, 75%); border: 1px solid rgba(102, 102, 154, 0.4); font-weight: 700;';
    } else if (monthly >= 8) {
      badgeName = `${getTyrisIcon('progreso', { size: 16 })} Ritmo Activo`;
      badgeColor = 'background-color: rgba(203, 153, 4, 0.2); color: var(--tyris-honor); border: 1px solid rgba(203, 153, 4, 0.4); font-weight: 700;';
    }

    // Días de la semana actual (Lunes a Domingo)
    const weekdays = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo'];
    
    // Mapear asistencias a fechas YYYY-MM-DD
    const attendedDates = new Set(
      (this.stats?.history || []).map(r => {
        return getUTCDateComponents(r.date).formattedDate;
      })
    );

    const today = new Date();
    const currentDay = today.getDay(); // 0 es Domingo, 1 es Lunes
    const distanceToMonday = currentDay === 0 ? 6 : currentDay - 1;
    const monday = new Date(today);
    monday.setDate(today.getDate() - distanceToMonday);
    monday.setHours(0, 0, 0, 0);

    const weekDates = Array.from({ length: 7 }, (_, idx) => {
      const d = new Date(monday);
      d.setDate(monday.getDate() + idx);
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    });

    const trainedDaysFlags = weekDates.map(dateStr => attendedDates.has(dateStr));

    return `
      <div class="dashboard-header">
        <div class="dashboard-title">
          <h2 style="display:flex;align-items:center;gap:0.6rem;">
            ${getTyrisIcon('asistencia', { size: 28 })} Registro de Asistencia
          </h2>
          <p>La consistencia vence al talento. Registra cada paso de tu viaje.</p>
        </div>
      </div>

      <!-- Cuadrícula de Métricas de Asistencia -->
      <div class="grid-stats" style="margin-bottom: 2rem;">
        <div class="stat-card">
          <div class="stat-icon" style="color: var(--tyris-iris);">${getTyrisIcon('asistencia', { size: 26 })}</div>
          <div class="stat-info">
            <span class="stat-value">${weekly}</span>
            <span class="stat-label">Esta Semana</span>
          </div>
        </div>
        
        <div class="stat-card">
          <div class="stat-icon" style="color: var(--tyris-energy);">${getTyrisIcon('racha', { size: 26 })}</div>
          <div class="stat-info">
            <span class="stat-value">${monthly}</span>
            <span class="stat-label">Este Mes</span>
          </div>
        </div>
        
        <div class="stat-card">
          <div class="stat-icon" style="color: var(--tyris-honor);">${getTyrisIcon('rankings', { size: 26 })}</div>
          <div class="stat-info">
            <span class="stat-value">${allTime}</span>
            <span class="stat-label">Total Histórico</span>
          </div>
        </div>
      </div>

      <div style="display:grid;grid-template-columns:repeat(auto-fit, minmax(320px, 1fr));gap:2rem;margin-bottom:2.5rem;">
        <!-- Check-in automático info -->
        <div class="card-glass" style="display:flex;flex-direction:column;justify-content:space-between;padding:2rem;">
          <div>
            <h3 style="font-size:1.35rem;margin-bottom:0.75rem;font-weight:700;display:flex;align-items:center;gap:0.5rem;">
              Asistencia Automática <span style="color:var(--tyris-energy);">${getTyrisIcon('éxito', { size: 22 })}</span>
            </h3>
            <p style="color:var(--text-muted);font-size:0.95rem;margin-bottom:1rem;line-height:1.6;">
              Tu asistencia se registra <strong>automáticamente</strong> cuando finalizas una sesión de entrenamiento.
              No necesitas hacer check-in manual.
            </p>
            <a href="#/workout" class="btn btn-primary" style="width:100%;padding:1rem;font-size:1rem;text-align:center;display:inline-flex;align-items:center;justify-content:center;gap:0.6rem;">
              ${getTyrisIcon('comenzar-entrenamiento', { size: 20 })} Ir a Entrenar
            </a>
          </div>
        </div>

        <!-- Ficha de Nivel de Disciplina (Gamificación) -->
        <div class="card-glass" style="padding: 2rem;">
          <h3 style="font-size: 1.35rem; margin-bottom: 0.5rem; font-weight: 700;">Nivel de Consistencia</h3>
          <div style="margin-bottom: 1.25rem;">
            <span class="badge" style="font-size: 0.85rem; padding: 0.4rem 0.9rem; display:inline-flex; align-items:center; gap:0.4rem; ${badgeColor}">
              ${badgeName}
            </span>
          </div>
          
          <p style="color: var(--text-muted); font-size: 0.9rem; line-height: 1.5; margin-bottom: 1.25rem;">
            Meta mensual sugerida: <strong>16 entrenamientos</strong> para un óptimo desarrollo de fuerza. ¡Llevas <strong>${monthly}</strong>!
          </p>

          <!-- Barra de Progreso Premium -->
          <div style="width: 100%; height: 8px; background: rgba(255,255,255,0.05); border-radius: var(--radius-full); overflow: hidden; margin-bottom: 0.5rem; border: 1px solid var(--border-metallic);">
            <div style="width: ${progressPercent}%; height: 100%; background: linear-gradient(90deg, var(--tyris-energy), var(--tyris-iris)); border-radius: var(--radius-full); transition: width 0.5s ease-out;"></div>
          </div>
          <div style="display: flex; justify-content: space-between; font-size: 0.75rem; color: var(--text-muted); font-weight: 600;">
            <span>0 días</span>
            <span>Meta: 16 días (${progressPercent.toFixed(0)}%)</span>
          </div>
        </div>
      </div>

      <!-- Cuadrícula Semanal Visual (Racha de Acero) -->
      <h3 style="font-size: 1.35rem; margin-bottom: 1rem; font-weight: 600; display:flex; align-items:center; gap:0.5rem;">
        <span style="color:var(--tyris-energy);">${getTyrisIcon('racha', { size: 24 })}</span> Racha de Entrenamiento Semanal
      </h3>
      <div class="card-glass" style="padding: 2rem; margin-bottom: 2rem;">
        <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(90px, 1fr)); gap: 1rem; text-align: center;">
          ${weekdays.map((day, idx) => {
            const isTrained = trainedDaysFlags[idx];
            const cardClass = isTrained ? 'streak-day-attended' : '';
            const bgStyle = isTrained
              ? '' 
              : 'background: rgba(255,255,255,0.02); border-color: var(--border-metallic);';
            const icon = isTrained
              ? getTyrisIcon('racha', { size: 28, className: 'streak-day-icon' })
              : getTyrisIcon('comenzar-entrenamiento', { size: 24, style: 'opacity:0.4;' });
            const textColor = isTrained ? '' : 'color: var(--text-muted);';

            return `
              <div class="${cardClass}" style="border: 1px solid; border-radius: var(--radius-md); padding: 1.25rem 0.5rem; ${bgStyle} transition: var(--transition-smooth); display:flex; flex-direction:column; align-items:center;">
                <div style="margin-bottom: 0.5rem; display:flex; align-items:center; justify-content:center; height:32px;">${icon}</div>
                <div class="streak-day-title" style="font-size: 0.8rem; ${textColor}">${day}</div>
                <div class="streak-day-sub" style="font-size: 0.65rem; color: ${isTrained ? '#0B3820' : 'rgba(255,255,255,0.4)'}; margin-top: 0.25rem;">
                  ${isTrained ? 'ENTRENADO' : 'Pendiente'}
                </div>
              </div>
            `;
          }).join('')}
        </div>
      </div>

      <!-- Calendario Mensual -->
      ${this.renderCalendar(this.stats?.history || [])}
    `;
  }

  afterRender(): void {}

  private renderCalendar(history: { date: string }[]): string {
    if (!history || history.length === 0) {
      return `
        <section class="card-glass">
          <h3 style="font-size: 1.35rem; margin-bottom: 1rem; font-weight: 600; display:flex; align-items:center; gap:0.5rem;">
            ${getTyrisIcon('asistencia', { size: 24 })} Calendario de Asistencias
          </h3>
          <div style="text-align: center; padding: 2rem; color: var(--text-muted);">
            Aún no tienes registros de asistencia en el sistema.
          </div>
        </section>
      `;
    }

    const byMonth: Record<string, string[]> = {};
    for (const r of history) {
      const comps = getUTCDateComponents(r.date);
      const key = `${comps.year}-${String(comps.month).padStart(2, '0')}`;
      if (!byMonth[key]) byMonth[key] = [];
      byMonth[key].push(r.date);
    }

    const months = Object.keys(byMonth).sort().reverse().slice(0, 3);

    const monthsHtml = months
      .map((monthKey) => {
        const [year, month] = monthKey.split('-').map(Number);
        const dates = new Set(byMonth[monthKey].map((d) => getUTCDateComponents(d).day));
        const firstDay = new Date(year, month - 1, 1).getDay();
        const daysInMonth = new Date(year, month, 0).getDate();
        const monthName = new Date(year, month - 1, 1).toLocaleDateString('es-ES', {
          month: 'long',
          year: 'numeric',
        });

        const cells: string[] = [];
        const offset = (firstDay + 6) % 7; // Mon=0
        for (let i = 0; i < offset; i++) cells.push(`<div></div>`);
        for (let d = 1; d <= daysInMonth; d++) {
          const attended = dates.has(d);
          const cellClass = attended ? 'cal-day cal-day-attended' : 'cal-day';
          const defaultStyle = 'background: rgba(255,255,255,0.02); border: 1px solid rgba(255,255,255,0.06); color: var(--text-muted); display: flex; align-items: center; justify-content: center; width: 34px; height: 34px; border-radius: 50%; font-size: 0.85rem; font-weight: 500; transition: var(--transition-fast);';
          
          cells.push(
            `<div class="${cellClass}" style="${attended ? 'display: flex; align-items: center; justify-content: center; width: 34px; height: 34px; border-radius: 50%; font-size: 0.85rem;' : defaultStyle}" title="${attended ? 'Asistió' : ''}">${d}</div>`,
          );
        }

        return `
          <div style="flex: 1; min-width: 240px; background: rgba(255,255,255,0.02); border: 1px solid var(--border-metallic); border-radius: var(--radius-md); padding: 1.25rem;">
            <h4 style="text-transform: capitalize; font-size: 1rem; margin-bottom: 1rem; text-align: center; color: var(--tyris-energy); font-weight: 700;">${monthName}</h4>
            <div style="display: grid; grid-template-columns: repeat(7, 1fr); gap: 0.5rem; text-align: center; justify-items: center;">
              <div style="font-weight: 600; color: var(--text-muted); font-size: 0.75rem;">L</div>
              <div style="font-weight: 600; color: var(--text-muted); font-size: 0.75rem;">M</div>
              <div style="font-weight: 600; color: var(--text-muted); font-size: 0.75rem;">X</div>
              <div style="font-weight: 600; color: var(--text-muted); font-size: 0.75rem;">J</div>
              <div style="font-weight: 600; color: var(--text-muted); font-size: 0.75rem;">V</div>
              <div style="font-weight: 600; color: var(--text-muted); font-size: 0.75rem;">S</div>
              <div style="font-weight: 600; color: var(--text-muted); font-size: 0.75rem;">D</div>
              ${cells.join('')}
            </div>
          </div>
        `;
      })
      .join('');

    return `
      <section class="card-glass" style="margin-top: 2rem;">
        <h3 style="font-size: 1.35rem; margin-bottom: 1.25rem; font-weight: 600; display:flex; align-items:center; gap:0.6rem;">
          <span style="color:var(--tyris-energy);">${getTyrisIcon('asistencia', { size: 24 })}</span> Historial de Asistencia (Últimos 3 meses)
        </h3>
        <div style="display: flex; gap: 1.5rem; flex-wrap: wrap;">
          ${monthsHtml}
        </div>
      </section>
    `;
  }

  private async fetchStats(): Promise<void> {
    try {
      this.stats = await apiClient.get<AttendanceStats>('/api/attendance/my-stats');
      this.errorMsg = null;
    } catch (err: any) {
      console.error('Error al recuperar métricas de asistencia:', err);
      this.errorMsg = err.message || 'Error de comunicación con el servidor.';
      toast.error('Error al sincronizar tu asistencia.');
    } finally {
      this.loading = false;
    }
  }
}
